import type {
  SurveyQuestion,
  SurveyQuestionWrite,
  SurveyTemplateWriteDto
} from '../types/survey';
import { isDefinedNumericLimit } from './surveyQuestions';

function appendNumericLimits(base: SurveyQuestionWrite, q: SurveyQuestion): SurveyQuestionWrite {
  if (q.questionType === 'SCALE') {
    return {
      ...base,
      minValue: q.minValue ?? null,
      maxValue: q.maxValue ?? null
    };
  }
  if (q.questionType === 'NUMBER') {
    const withLimits: SurveyQuestionWrite = { ...base };
    if (isDefinedNumericLimit(q.minValue)) withLimits.minValue = q.minValue;
    if (isDefinedNumericLimit(q.maxValue)) withLimits.maxValue = q.maxValue;
    return withLimits;
  }
  return base;
}

export function buildSurveyTemplateWriteDto(input: {
  title: string;
  description: string | null;
  active: boolean;
  templateVersion: number;
  validFrom: string | null;
  validTo: string | null;
  questions: SurveyQuestion[];
}): SurveyTemplateWriteDto {
  const questions: SurveyQuestionWrite[] = input.questions.map((q, idx) => {
    if (q.questionType === 'INSTRUCTION') {
      return {
        sortOrder: idx + 1,
        questionText: q.questionText.trim(),
        questionType: 'INSTRUCTION',
        required: false,
        options: []
      };
    }
    const isChoice = q.questionType === 'SINGLE_CHOICE' || q.questionType === 'MULTI_CHOICE';
    const base: SurveyQuestionWrite = {
      sortOrder: idx + 1,
      questionText: q.questionText.trim(),
      questionType: q.questionType,
      required: Boolean(q.required),
      options: isChoice
        ? q.options.map((opt, oi) => ({
            sortOrder: oi + 1,
            label: opt.label.trim(),
            optionValue: opt.optionValue.trim()
          }))
        : []
    };
    return appendNumericLimits(base, q);
  });

  return {
    title: input.title.trim(),
    description: input.description?.trim() || null,
    active: input.active,
    templateVersion: input.templateVersion,
    validFrom: input.validFrom || null,
    validTo: input.validTo || null,
    questions
  };
}

export function normalizeQuestionsForForm(questions: SurveyQuestion[]): SurveyQuestion[] {
  return [...questions]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((q, idx) => ({
      ...q,
      sortOrder: idx + 1,
      options: [...(q.options || [])].sort((a, b) => a.sortOrder - b.sortOrder)
    }));
}
